import { useState } from 'react';
import { Loader2, CheckCircle, XCircle, ChevronUp, ChevronDown, X } from 'lucide-react';
import { useJobTracker } from '../../providers/JobTrackerProvider';

export default function JobTrackerPanel() {
  const { jobs, activeCount, removeJob, clearCompleted } = useJobTracker();
  const [expanded, setExpanded] = useState(false);

  if (jobs.length === 0) return null;

  const completedCount = jobs.filter(j => j.status !== 'running').length;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
      {/* Header bar — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-muted hover:bg-accent transition-colors"
      >
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
          )}
          <span className="text-sm font-medium text-foreground">
            {activeCount > 0
              ? `${activeCount} job${activeCount > 1 ? 's' : ''} running`
              : `${completedCount} job${completedCount > 1 ? 's' : ''} finished`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {completedCount > 0 && activeCount === 0 && (
            <span
              onClick={(e) => { e.stopPropagation(); clearCompleted(); }}
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer mr-1"
            >
              Clear
            </span>
          )}
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {/* Job list */}
      {expanded && (
        <div className="max-h-60 overflow-y-auto divide-y divide-border">
          {jobs.map(job => (
            <div key={job.id} className="px-4 py-2.5 flex items-start gap-2">
              <div className="mt-0.5 flex-shrink-0">
                {job.status === 'running' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                {job.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                {job.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{job.label}</p>
                {job.status === 'running' && (
                  <p className="text-xs text-muted-foreground">
                    {formatElapsed(job.startedAt)}
                  </p>
                )}
                {job.error && (
                  <p className="text-xs text-red-500 truncate">{job.error}</p>
                )}
              </div>
              {job.status !== 'running' && (
                <button
                  onClick={() => removeJob(job.id)}
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatElapsed(isoTimestamp: string): string {
  const elapsed = Date.now() - new Date(isoTimestamp).getTime();
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}
